/** WARNING: This file is generated automatically **/

/**
 * collection of attributes for casino games
 */
export interface CasinoGame {
  /**
   * unique identifier of the game (matches XC_TABLETYPES.GAMETYPE)
   */
  gameId: number;
  /**
   * unique identifier of the game variant (matches XC_TABLETYPES.VARIANTTYPE)
   */
  variantId: number;
  /**
   * name of the game variant
   */
  gameName: string | null;
  /**
   * name of the game category
   */
  gameCategory?: string | null;
  /**
   * name of the vendor
   */
  vendorName?: string | null;
  /**
   * value of the jackpot in cents (or equivalent in other CURR) at the time the user interacts with the game (not all games have jackpot)
   */
  jackpotValue?: number | null;
  /**
   * currency code of the jackpot value that is presented to the player (source: currency.cpp)
   */
  jackpotCurrency?:
    | (
        | 'AUD'
        | 'CAD'
        | 'DKK'
        | 'EUR'
        | 'GBP'
        | 'NOK'
        | 'SEK'
        | 'USD'
        | 'RUB'
        | 'NZD'
        | 'CHF'
        | 'CZK'
        | 'BRL'
        | 'BGN'
        | 'CLP'
        | 'COP'
        | 'HRK'
        | 'HUF'
        | 'MXN'
        | 'KZT'
        | 'LTL'
        | 'PYG'
        | 'PEN'
        | 'PLN'
        | 'RON'
        | 'RSD'
        | 'UAH'
        | 'MDL'
        | 'PHP'
        | 'ARS'
        | 'CNY'
        | 'EEK'
        | 'HKD'
        | 'INR'
        | 'IDR'
        | 'KRW'
        | 'LVL'
        | 'MYR'
        | 'SGD'
        | 'ZAR'
        | 'TWD'
        | 'THB'
        | 'VND'
        | 'ISK'
        | 'JPY'
        | 'BYR'
      )
    | null;
  /**
   * the position of the game within the component (widget, CRM zone etc)
   */
  gamePosition?: number | null;
  /**
   * the tags that are assigned to the game
   */
  gameTags?: string[] | null;
  /**
   * is the game PM (PFF) or not
   */
  isPlayMoney?: boolean | null;
}

export const casinoGame = (data: Readonly<CasinoGame>) => ({
  schema: 'iglu:com.starsgroup/casino_game/jsonschema/2-0-3',
  data
});

/**
 * attributes of casino race
 */
export interface CasinoRace {
  /**
   * unique identfier of the race. matches XC_RACES.RACEID
   */
  raceId: string;
  /**
   * the name of the race. matches XC_RACES.NAME
   */
  raceName: string;
  /**
   * the total number of players that registered to the the race. If race is active - this field should show the number of players currently playing.
   */
  numberOfPlayers: number;
  /**
   * the current status of the race. matches values in XC_RACES.STATUS
   */
  raceStatus:
    | 'NONE'
    | 'CREATED'
    | 'ANNOUNCED'
    | 'REGOPEN'
    | 'STARTED'
    | 'REGCLOSED'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'ABORTED';
}

export const casinoRace = (data: Readonly<CasinoRace>) => ({
  schema: 'iglu:com.starsgroup/casino_race/jsonschema/1-0-0',
  data
});

/**
 * attributes of registered users
 */
export interface User {
  /**
   * total RM balance in the account from all wallets in USD
   */
  rmBalance: number;
}

export const user = (data: Readonly<User>) => ({
  schema: 'iglu:com.starsgroup/user/jsonschema/1-0-0',
  data
});

/**
 * Attributes of a desktop screen
 */
export interface DesktopScreen {
  /**
   * The name of the screen viewed
   */
  name: string;
  /**
   * the tab path that the user was on when they performed the action
   */
  TabPath?: string | null;
}

export const desktopScreen = (data: Readonly<DesktopScreen>) => ({
  schema: 'iglu:com.starsgroup/desktop_screen/jsonschema/1-0-2',
  data
});

/**
 * Attributes of crm content package
 */
export interface CrmPackage {
  /**
   * the name of the content package in the zone the user is interacting with
   */
  packageName: string;
  /**
   * unique identifier of the content package
   */
  packageId: number;
}

export const crmPackage = (data: Readonly<CrmPackage>) => ({
  schema: 'iglu:com.starsgroup/crm_package/jsonschema/1-0-0',
  data
});

/**
 * Attrinutes of poker cash games
 */
export interface PokerCashGame {
  /**
   * ID that describes the table features. Matches values in Tabtypes.
   */
  typeId: number;
}

export const pokerCashGame = (data: Readonly<PokerCashGame>) => ({
  schema: 'iglu:com.starsgroup/poker_cash_game/jsonschema/1-0-0',
  data
});

/**
 * Selection of attributes for completed deposits
 */
export interface Deposit {
  /**
   * the amount of the deposit
   */
  amount: number;
  /**
   * the status of the deposit
   */
  status: string;
  /**
   * the currency code of the deposit (as per currency.cpp)
   */
  currency: string;
  /**
   * code of the pay system used to deposit the funds
   */
  paySystemCode: number;
}

export const deposit = (data: Readonly<Deposit>) => ({
  schema: 'iglu:com.starsgroup/deposit/jsonschema/1-0-0',
  data
});

/**
 * Schema for sports_bet entity
 */
export interface SportsBet {
  /**
   * the unique id of the bet, or null if placement fails
   */
  betId: number;
  /**
   * the stake of the bet
   */
  betStake: number;
  /**
   * the currency used to place the bet
   */
  betCurrency:
    | 'PFF'
    | 'USD'
    | 'EUR'
    | 'CAD'
    | 'GBP'
    | 'DKK'
    | 'INR'
    | 'RUB'
    | 'SEK'
    | 'UAH'
    | 'RON';
  /**
   * the type of the bet
   */
  betType:
    | 'SINGLE'
    | 'DOUBLE'
    | 'TREBLE'
    | 'FOURFOLD'
    | 'FIVEFOLD'
    | 'SIXFOLD'
    | 'SEVENFOLD'
    | 'EIGHTFOLD'
    | 'NINEFOLD'
    | 'TENFOLD'
    | 'ELEVENFOLD'
    | 'TWELVEFOLD'
    | 'THIRTEENFOLD'
    | 'FOURTEENFOLD'
    | 'FIFTEENFOLD'
    | 'SIXTEENFOLD'
    | 'SEVENTEENFOLD'
    | 'EIGHTEENFOLD'
    | 'NINETEENFOLD'
    | 'TWENTYFOLD'
    | 'TWENTYONEFOLD'
    | 'TWENTYTWOFOLD'
    | 'TWENTYTHREEFOLD'
    | 'TWENTYFOURFOLD'
    | 'TWENTYFIVEFOLD'
    | 'TRIXIE'
    | 'PATENT'
    | 'YANKEE'
    | 'CANADIAN'
    | 'HEINZ'
    | 'SUPER_HEINZ'
    | 'GOLIATH'
    | 'LUCKY_15'
    | 'LUCKY_31'
    | 'LUCKY_63';
  /**
   * the category of the bet
   */
  betCategory?: 'STANDARD' | 'BET_BUILDER' | 'BET_BUILDER_PLUS';
  /**
   * the potential returns from the bet
   */
  betPotentialReturn?: number | null;
  /**
   * is cash out available for the bet
   */
  betCashoutAvailable: boolean;
  /**
   * if cashout is available, the price offered
   */
  betCashoutPrice?: number | null;
  /**
   * has the bet been cashed out
   */
  betCashedOut: boolean;
  /**
   * the time the bet was placed
   */
  betPlacedTime: number;
  /**
   * is the bet a starting price bet
   */
  isStartingPrice: boolean;
}

export const sportsBet = (data: Readonly<SportsBet>) => ({
  schema: 'iglu:com.starsgroup/sports_bet/jsonschema/3-0-2',
  data
});

/**
 * Schema for sports_race entity
 */
export interface SportsRace {
  /**
   * unique identifier of the race
   */
  raceId: string;
}

export const sportsRace = (data: Readonly<SportsRace>) => ({
  schema: 'iglu:com.starsgroup/sports_race/jsonschema/1-0-1',
  data
});

/**
 * attributes of crm zones
 */
export interface CrmZone {
  /**
   * name/code/tag of the zone
   */
  zoneName: string;
}

export const crmZone = (data: Readonly<CrmZone>) => ({
  schema: 'iglu:com.starsgroup/crm_zone/jsonschema/2-0-0',
  data
});

/**
 * Schema for sports_event entity
 */
export interface SportsEvent {
  /**
   * unique identifier of the event
   */
  eventId: number;
  /**
   * the start time of the competition event
   */
  eventStartTime?: number | null;
}

export const sportsEvent = (data: Readonly<SportsEvent>) => ({
  schema: 'iglu:com.starsgroup/sports_event/jsonschema/3-0-0',
  data
});

/**
 * collection of attributes that describe the app the user interacts with (applies to all the evetns)
 */
export interface App {
  /**
   * unique identifier of the brand, based on brands.h
   */
  brandId: number;
  /**
   * unique identifier of the site, based on sites.h
   */
  siteId: number;
  /**
   * the language the app/webpage is set to (not browser language) - en, de, fr and not English, German, French etc
   */
  languageCode:
    | (
        | 'en'
        | 'en-GB'
        | 'en-US'
        | 'fr'
        | 'de'
        | 'es'
        | 'ru'
        | 'pt'
        | 'nl'
        | 'it'
        | 'pl'
        | 'hu'
        | 'da'
        | 'sv'
        | 'fi'
        | 'no'
        | 'ja'
        | 'zh-CN'
        | 'zh-TW'
        | 'bg'
        | 'cs'
        | 'ro'
        | 'sl'
        | 'uk'
        | 'el'
        | 'et'
        | 'lt'
        | 'lv'
        | 'pt-BR'
        | 'hr'
        | 'ko'
        | 'es-419'
        | 'sk'
        | 'vi'
        | 'is'
        | 'kl'
        | 'en-IN'
      )
    | null;
  /**
   * in sports, this is the format in which the odds are presented to the user
   */
  oddsFormat?: ('DECIMAL' | 'FRACTIONAL' | 'AMERICAN') | null;
  /**
   * play mode the app is set to
   */
  playMode: 'RM' | 'PM';
  /**
   * version of the application being used
   */
  version?: string | null;
  /**
   * A unique identifier of the Client Int attribute, based on experiment group.
   */
  ClientIntID?: string | null;
  /**
   * this will determine if it's the Android App or Desktop Client.
   */
  isEmbedded?: boolean | null;
}

export const app = (data: Readonly<App>) => ({
  schema: 'iglu:com.starsgroup/app/jsonschema/3-0-7',
  data
});

/**
 * Selection of attributes for completed withdrawals
 */
export interface Withdrawal {
  /**
   * the withdrawal amount
   */
  amount: number;
  /**
   * the withdrawal status
   */
  status: string;
  /**
   * the withdrawal currency code (as per currency.cpp)
   */
  currency: string;
}

export const withdrawal = (data: Readonly<Withdrawal>) => ({
  schema: 'iglu:com.starsgroup/withdrawal/jsonschema/1-0-1',
  data
});

/**
 * collection of attributes for poker tournaments
 */
export interface PokerTournament {
  /**
   * Unique ID of the tournament.
   */
  tournamentId?: number | null;
  /**
   * the total prizepool collected at the time of the event.
   */
  prizePool?: number | null;
  /**
   * Total number of players currently registered into the tournament.
   */
  registeredPlayers?: number | null;
  /**
   * The number of players that are left in the tournament at the time of the event.
   */
  playersLeft?: number | null;
  /**
   * Indicates if the red spade sign appears.
   */
  isTeamPlayerIn?: boolean | null;
  /**
   * the Buy-in amount of the tournament.
   */
  buyIn: number;
  /**
   * Report the currency of the tournament uses.
   */
  currency: string;
  /**
   * Indicates whether the game is a Hold'em, Omaha, HORSE, 8-Game, etc.
   */
  gameType: string;
}

export const pokerTournament = (data: Readonly<PokerTournament>) => ({
  schema: 'iglu:com.starsgroup/poker_tournament/jsonschema/1-0-0',
  data
});

/**
 * Table information for cash tables
 */
export interface PokerCashTableStats {
  /**
   * For zoom games only - the name of the table in which the game is played
   */
  tableName?: string | null;
  /**
   * Total number of players currently seating at the table. For zoom games, this number will represent the total number of players participating.
   */
  playersSeated?: number | null;
  /**
   * For zoom games only - Indicates if the early bird sign is presented.
   */
  isEarlyBird?: boolean | null;
  /**
   * For ring games only - The number of players in the waiting list.
   */
  waitingPlayers?: number | null;
  /**
   * AVG pot size of the table.
   */
  avgPot?: number | null;
  /**
   * AVG stack size at the table.
   */
  avgStack?: number | null;
  /**
   * fraction representing the percentage of players that see a flop.
   */
  playersPerFlop?: number | null;
  /**
   * fraction representing VPIP of the table.
   */
  vpip?: number | null;
  /**
   * Hands per hour.
   */
  handsPerHour?: number | null;
}

export const pokerCashTableStats = (data: Readonly<PokerCashTableStats>) => ({
  schema: 'iglu:com.starsgroup/poker_cash_table_stats/jsonschema/1-0-0',
  data
});

/**
 * Schema for sports_selection entity
 */
export interface SportsSelection {
  /**
   * the unique identifier for the selection
   */
  selectionId: number;
  /**
   * the status of the selection at the time of the tracking event
   */
  selectionStatus: 'ACTIVE' | 'SUSPENDED' | 'REMOVED';
  /**
   * the decimal odds of the selection at the time of the tracking event
   */
  selectionOdds?: number | null;
  /**
   * is the selection a starting price selection
   */
  isStartingPrice: boolean;
}

export const sportsSelection = (data: Readonly<SportsSelection>) => ({
  schema: 'iglu:com.starsgroup/sports_selection/jsonschema/2-0-0',
  data
});

/**
 * Schema for sports_market entity
 */
export interface SportsMarket {
  /**
   * unique identifier for the market
   */
  marketId: string;
  /**
   * is the market in play at the time of the tracking event
   */
  marketInPlay: boolean;
  /**
   * is the market an odds boost market
   */
  marketIsOddsBoost: boolean;
}

export const sportsMarket = (data: Readonly<SportsMarket>) => ({
  schema: 'iglu:com.starsgroup/sports_market/jsonschema/2-0-0',
  data
});

/**
 * Schema for sports_sport entity
 */
export interface SportsSport {
  /**
   * unique identifier of the sport
   */
  sportId: number;
}

export const sportsSport = (data: Readonly<SportsSport>) => ({
  schema: 'iglu:com.starsgroup/sports_sport/jsonschema/1-0-0',
  data
});

/**
 * collection of attributes for a widget
 */
export interface Widget {
  /**
   * the name of the widget
   */
  widgetName: string;
  /**
   * unique identifier of the widget
   */
  widgetId: string;
  /**
   * optional widget context for further information
   */
  widgetContext?: string | null;
}

export const widget = (data: Readonly<Widget>) => ({
  schema: 'iglu:com.starsgroup/widget/jsonschema/1-0-2',
  data
});

/**
 * Schema for sports_competition entity
 */
export interface SportsCompetition {
  /**
   * unique identifier of the competition
   */
  competitionId: number;
}

export const sportsCompetition = (data: Readonly<SportsCompetition>) => ({
  schema: 'iglu:com.starsgroup/sports_competition/jsonschema/1-0-0',
  data
});

/**
 * Schema for a bonus entity
 */
export interface Bonus {
  /**
   * the value of the bonus
   */
  amount: number;
  /**
   * determines whether or not the bonus cash element of the stake is returned in the win
   */
  awardingType: 'REDEEMABLE' | 'NON_REDEEMABLE';
  /**
   * the currency of the bonus
   */
  currency:
    | 'AUD'
    | 'GBP'
    | 'BGN'
    | 'CAD'
    | 'CLP'
    | 'DKK'
    | 'EUR'
    | 'HUF'
    | 'INR'
    | 'JPY'
    | 'MXN'
    | 'NOK'
    | 'PFF'
    | 'PLN'
    | 'RON'
    | 'RUB'
    | 'SEK'
    | 'CHF'
    | 'UAH'
    | 'USD'
    | 'BRL';
  /**
   * Date of expiry of the bonus
   */
  expiry: number;
  /**
   * id of the wallet
   */
  id: string;
  /**
   * the type of the bonus
   */
  type: 'BONUS_CASH';
  /**
   * types of bet flags
   */
  betTypeFlags?: {
    single: boolean;
    accumulator: boolean;
    system: boolean;
    reserved: boolean;
    inPlay: boolean;
    preGame: boolean;
  };
  /**
   * list of eligible sports / events
   */
  eligibleSports?: string[];
  eligibleEvents?: string[];
}

export const bonus = (data: Readonly<Bonus>) => ({
  schema: 'iglu:com.starsgroup/bonus/jsonschema/1-0-1',
  data
});

export interface Entity<
  T =
    | CasinoGame
    | CasinoRace
    | User
    | DesktopScreen
    | CrmPackage
    | PokerCashGame
    | Deposit
    | SportsBet
    | SportsRace
    | CrmZone
    | SportsEvent
    | App
    | Withdrawal
    | PokerTournament
    | PokerCashTableStats
    | SportsSelection
    | SportsMarket
    | SportsSport
    | Widget
    | SportsCompetition
    | Bonus
> {
  schema: string;
  data: Readonly<T>;
}
